import React from 'react';

import DeepLinkPage from './DeepLinkPage';
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL } from '../constants/appStore';

// Storage key for pending deep links
const PENDING_DEEP_LINK_KEY = 'rollops_pending_deep_link';

/**
 * Detects if the RollOps app is installed by attempting to open a deep link
 * and checking if the page loses focus (indicating app opened)
 */
export function detectAppInstalled(deepLink: string): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve(false); // Assume not installed if timeout
      }
    }, 2000); // 2 second timeout

    // Track if page loses focus (app opened)
    const handleBlur = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve(true);
        window.removeEventListener('blur', handleBlur);
      }
    };

    // Track if page visibility changes (app opened)
    const handleVisibilityChange = () => {
      if (document.hidden && !resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve(true);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Try to open the deep link
    try {
      window.location.href = deepLink;
    } catch (e) {
      // If we can't even try to open it, assume not installed
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolve(false);
        window.removeEventListener('blur', handleBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    }

    // Fallback: if still not resolved after timeout, check if we're still focused
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        // If page is still focused/visible, app likely didn't open
        resolve(!document.hidden && document.hasFocus());
        window.removeEventListener('blur', handleBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    }, 1500);
  });
}

/**
 * Stores a deep link to be processed after app installation
 */
export function storePendingDeepLink(deepLink: string): void {
  try {
    sessionStorage.setItem(PENDING_DEEP_LINK_KEY, deepLink);
  } catch (e) {
    // Fallback to localStorage if sessionStorage fails
    try {
      localStorage.setItem(PENDING_DEEP_LINK_KEY, deepLink);
    } catch {
      // Ignore if storage is unavailable
    }
  }
}

/**
 * Retrieves and clears a pending deep link
 */
export function getPendingDeepLink(): string | null {
  try {
    const link = sessionStorage.getItem(PENDING_DEEP_LINK_KEY) || localStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (link) {
      sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
      localStorage.removeItem(PENDING_DEEP_LINK_KEY);
    }
    return link;
  } catch {
    return null;
  }
}

/**
 * Detects the user's platform
 */
export function detectPlatform(): 'ios' | 'android' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

/**
 * Gets the appropriate App Store URL for the platform
 */
export function getAppStoreUrl(platform?: 'ios' | 'android' | 'desktop'): string {
  const detectedPlatform = platform || detectPlatform();
  
  if (detectedPlatform === 'ios') {
    return IOS_APP_STORE_URL;
  }
  if (detectedPlatform === 'android') {
    return ANDROID_PLAY_STORE_URL;
  }
  // Default to iOS for desktop users
  return IOS_APP_STORE_URL;
}

/**
 * Opens the App Store and stores the deep link for later processing
 */
export function redirectToAppStore(deepLink: string, platform?: 'ios' | 'android' | 'desktop'): void {
  storePendingDeepLink(deepLink);
  const storeUrl = getAppStoreUrl(platform);
  window.open(storeUrl, '_blank');
}

export function buildScheme(basePath: string) {
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const scheme = isLocalhost ? 'rollops-dev' : 'rollops';
  const sanitizedPath = basePath.startsWith('/') ? basePath.slice(1) : basePath;
  return `${scheme}://${sanitizedPath}`;
}

/**
 * Convert a deep link URL like `rollops://login`,
 * `rollops://draft-invite/<token>`, or `rollops-dev://reset-password?token=x`
 * into the equivalent web path (`/login`, `/draft-invite/<token>`,
 * `/reset-password?token=x`). Returns `/` for empty / unrecognized inputs.
 *
 * The inverse of buildScheme(): since buildScheme strips the leading slash
 * before joining with the scheme, the part after `://` already IS the desired
 * web path — we just need to re-add a single leading slash.
 */
export function deepLinkToWebPath(deepLink: string): string {
  try {
    const withoutScheme = deepLink.replace(/^[a-z-]+:\/\/+/i, '');
    const normalizedPath = withoutScheme.replace(/^\/+/, '');
    return normalizedPath ? `/${normalizedPath}` : '/';
  } catch {
    return '/';
  }
}

export function renderDeepLinkPage(props: React.ComponentProps<typeof DeepLinkPage>) {
  return <DeepLinkPage {...props} />;
}
